import { useState } from 'react';
import styled from 'styled-components';
import { Dialog, DialogContent, DialogContentText, DialogActions, TextField } from '@material-ui/core';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { HealtheraButton, SecondaryButton } from '../../healtheraComponents';
import chainsApi from '../../apis/chains';

const TYPES = {
  BRANCH: 'branch',
  WEBSITE: 'website',
  CUSTOM: 'custom',
};

const MAX_MESSAGE_LENGTH = 160;
const MIN_MESSAGE_LENGTH = 20;
const LINK_AND_OPT_OUT_LENGTH = 80;
const MAX_CUSTOM_MESSAGE_LENGTH = MAX_MESSAGE_LENGTH - LINK_AND_OPT_OUT_LENGTH;

function ResendTextInvites({ organisation }) {
  const { enqueueSnackbar } = useSnackbar();

  const [type, setType] = useState(null);
  const [customMessage, setCustomMessage] = useState('');

  const organisationId = organisation?._id;
  const organisationHasPlusWebsite = Boolean(
    organisation?.website_configuration?.deployed && organisation?.website_configuration?.domain
  );

  const modalMessages = {
    branch: 'Confirm that you want to send an MTI from all branches of this organisation',
    website: 'Confirm that you want to send an MTI for this organisation’s Plus Website',
    custom: 'Enter the messsage to send as SMS and confirm',
  };

  const handleCustomMessageChange = (e) => {
    const input = e.target.value;
    if (input.length <= MAX_CUSTOM_MESSAGE_LENGTH) {
      setCustomMessage(input);
    }
  };

  const { mutateAsync: sendTextInvites, isLoading: isSendingInvites } = useMutation({
    mutationFn: () => chainsApi.resendTextInvites({ organisationId, type, customMessage }),
    onSuccess: (response) => {
      const invitesSent = response?.data?.[0]?.total || 0;
      enqueueSnackbar(`${invitesSent} text invites sent successfully`, { variant: 'success' });
      setType(null);
      setCustomMessage('');
    }
  });

  return (
    <>
      <ButtonsContainer>
        <HealtheraButton onClick={() => setType(TYPES.BRANCH)}>Send branch MTI</HealtheraButton>
        {organisationHasPlusWebsite && (
          <HealtheraButton onClick={() => setType(TYPES.WEBSITE)}>Send plus MTI</HealtheraButton>
        )}
        <HealtheraButton onClick={() => setType(TYPES.CUSTOM)} $fontsize={'14px'}>Custom Re-send all MTI</HealtheraButton>
      </ButtonsContainer>
      <Dialog open={Boolean(type)} onClose={() => setType(null)}>
        <DialogContent>
          <DialogContentText style={{paddingBottom: '10px'}}>{modalMessages[type]}</DialogContentText>
          {type === TYPES.CUSTOM && (
            <>
              <TextField
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                value={customMessage}
                onChange={handleCustomMessageChange}
                placeholder="Enter your message here (the link and opt-out text will be appended to this message)..."
                inputProps={{ maxLength: MAX_CUSTOM_MESSAGE_LENGTH }}
                autoFocus
              />
              <CharacterCount>
                Character count: {customMessage.length} / {MAX_CUSTOM_MESSAGE_LENGTH} (max {MAX_MESSAGE_LENGTH} characters with link and opt-out text)
              </CharacterCount>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ display: 'flex', justifyContent: 'center', gap: '16px', padding: '24px' }}>
          <HealtheraButton
            disabled={isSendingInvites || (type === TYPES.CUSTOM && customMessage.length < MIN_MESSAGE_LENGTH)}
            onClick={sendTextInvites}
          >
            Send
          </HealtheraButton>
          <SecondaryButton
            disabled={isSendingInvites}
            onClick={() => {
              setType(null);
              setCustomMessage('');
            }}
          >
            Cancel
          </SecondaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ResendTextInvites;

const ButtonsContainer = styled.div`
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CharacterCount = styled.p`
  text-align: right;
  font-size: 0.875rem;
  color: #888;
  margin-top: 8px;
`;
